<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  quote: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);

const { t } = useI18n();
const dialogRef = ref(null);
const rejectionReason = ref('');

const canConfirm = computed(
  () => !!rejectionReason.value.trim() && !props.isLoading
);

const quoteLabel = computed(() => {
  if (!props.quote) return '';
  return (
    props.quote.name ||
    t('PLANNING.SHOW.QUOTE_VERSION_SHORT', {
      version: props.quote.version,
    })
  );
});

watch(
  () => props.modelValue,
  async open => {
    if (open) {
      rejectionReason.value = '';
      await nextTick();
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  },
  { immediate: true }
);

const handleConfirm = () => {
  const reason = rejectionReason.value.trim();
  if (!reason || props.isLoading) return;
  emit('confirm', reason);
};

const handleClose = reason => {
  emit('update:modelValue', false);
  if (reason === 'cancel' || reason === 'dismiss' || reason == null) {
    emit('cancel');
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    width="md"
    :title="t('PLANNING.SHOW.REJECT_QUOTE_MODAL.TITLE')"
    :description="
      t('PLANNING.SHOW.REJECT_QUOTE_MODAL.DESCRIPTION', { quote: quoteLabel })
    "
    :confirm-button-label="t('PLANNING.SHOW.CONFIRM_REJECT')"
    :cancel-button-label="t('PLANNING.EVENTS.CANCEL')"
    :disable-confirm-button="!canConfirm"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="handleClose"
    @cancel="handleClose"
  >
    <div class="flex flex-col gap-2">
      <label
        class="block text-sm font-medium text-n-slate-11"
        for="quote-rejection-reason"
      >
        {{ t('PLANNING.SHOW.REJECTION_REASON') }}
        <span class="text-r-500">*</span>
      </label>
      <TextArea
        id="quote-rejection-reason"
        v-model="rejectionReason"
        :placeholder="t('PLANNING.SHOW.REJECT_QUOTE_MODAL.REASON_PLACEHOLDER')"
        :disabled="isLoading"
        :max-length="500"
        auto-height
        min-height="5rem"
      />
    </div>
  </Dialog>
</template>
